import {observer} from 'mobx-react';
import moment from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Col, Row} from 'reactstrap';
import {from} from 'rxjs';
import {share} from 'rxjs/operators';
import styled from 'styled-components';
import HorizontalStackedBar from '../../components/charts/horizontalStackedBar';
import VerticalBar from '../../components/charts/verticalBar';
import {
    activeLabelsArray,
    buildGroupQuery,
    calculeDateGroup,
    communicationLabelsArray,
    communicationTime,
    createLabelIcon,
    getDateGroup,
    getDeviceActiveGroup,
    getDeviceActiveGroupIdByIndex,
    getSinisterGroup,
    getSinisterGroupIdByIndex,
    getStateGroupIdByIndex,
    getStatusGroup,
    getTypeGroupPercent,
    sinisterDurationLabelsArray,
    statusLabelsArray,
} from '../../helpers/admin';
import useStore from '../../hooks/use-store';
import history from '../../routes/history';
import '../../views/admin/admin.sass';
import ChartCard from '../../views/admin/chartCard';
import '../../views/big-fleet/big-fleet.sass';
import I18nMessage from 'src/components/I18nMessage';
import {useIntl} from 'react-intl';

const StyledSmall = styled.small`
  color: #666666;
`;

// Cinco minutos: o painel resume estado e ultima comunicacao da frota, entao dado recente basta.
// Quem sai da tela desmonta o container e cancela o intervalo; voltar ao painel recalcula na hora.
const DASHBOARD_REFRESH_MS = 300000;

const AdminDashboardContainer = ({children, ...props}) => {
    const intl = useIntl();

    const [communicationGroup, setCommunicationGroup] = useState({});
    const [stateGroup, setStateGroup] = useState({});
    const [deviceGroup, setDeviceGroup] = useState({});
    const [sinisterGroup, setSinisterGroup] = useState({});

    const [typeStatusChartData, setTypeStatusChartData] = useState([]);

    const {store} = useStore();
    const {devices, sinisterStore} = store;

    // Os numeros do painel resumem estado e ultima comunicacao da frota, entao precisam de dado
    // recente -- nao de dado instantaneo. Antes a dependencia era `devices.list`, derivado que
    // serializa a colecao inteira e muda de identidade a cada leitura: cada posicao recebida
    // derrubava e remontava as quatro agregacoes sobre a frota toda, varias vezes por segundo, e
    // cada uma terminava em setState, realimentando o ciclo. Agora o recalculo acontece quando a
    // frota muda de tamanho ou a cada intervalo, e a leitura do derivado fica dentro do efeito,
    // fora do que o componente observa.
    const [refreshTick, setRefreshTick] = useState(0);
    const deviceCount = devices.devices.length;

    useEffect(() => {
        const timer = setInterval(() => setRefreshTick((tick) => tick + 1), DASHBOARD_REFRESH_MS);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        // Os quatro graficos contam por estado, categoria e ultima comunicacao. `list` entregaria
        // cada dispositivo inteiro, com buffer de posicoes junto, e e o que fazia o painel demorar
        // para montar conforme a frota cresce.
        const deviceList = devices.summaryList;

        if (deviceList.length) {
            const source$ = from(deviceList).pipe(share());

            const subscriptions = [
                getDeviceActiveGroup(source$).subscribe((data) => {
                    setDeviceGroup(data);
                }),
                getDateGroup(source$, communicationTime).subscribe((dateGroup) => {
                    setCommunicationGroup(dateGroup);
                }),
                getStatusGroup(source$).subscribe((dateGroup) => {
                    setStateGroup(dateGroup);
                }),
                getTypeGroupPercent(source$).subscribe((data) => {
                    setTypeStatusChartData(data);
                }),
            ];

            return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
        }
        return undefined;
    }, [deviceCount, refreshTick]);

    useEffect(()=>{
        if (sinisterStore.incidents.length > 0) {
            const source$ = from(sinisterStore.incidents.filter((s) => s.status === 'opened')).pipe(share());
            const subscription = getSinisterGroup(source$).subscribe((data) => setSinisterGroup(data));

            return () => subscription.unsubscribe();
        }
        return undefined;
    }, [sinisterStore.incidents]);

    const defaultColors = useMemo(
        () => ['#00b105', '#fe7f00', '#f03528', '#8a038c', '#abaaac'],
        [],
    );

    const squareLabel = useCallback(
        (color) => <span className="fas fa-square-full" style={{color}}/>,
        [],
    );

    const statusData = useMemo(
        () => ({
            colors: [defaultColors[0], defaultColors[4], '#FFD800'],
            data: typeStatusChartData.map(({name, ...rest}) => Object.values(rest)),
            labels: typeStatusChartData.map(({name}) => createLabelIcon(name)),
            // The chart indexes this by segment, so it needs rendered strings.
            labelsName: statusLabelsArray.map((label) => intl.formatMessage(label)),
        }),
        [typeStatusChartData, defaultColors, intl],
    );

    const communicationData = useMemo(() => {
        const values = Object.values(communicationGroup);

        return {
            colors: defaultColors,
            data: calculeDateGroup(values),
            amount: values.map((d) => d.length),
            legends: communicationLabelsArray.map((label, i) => ({
                element: squareLabel(defaultColors[i]),
                label: intl.formatMessage(label),
            })),
        };
    }, [communicationGroup, defaultColors, intl, squareLabel]);

    const sinisterData = useMemo(() => {
        const values = Object.values(sinisterGroup);
        const colors = ['#FDA700', '#8A008B', '#0031B0', '#6B6B6B'];

        return {
            colors: colors,
            data: calculeDateGroup(values),
            amount: values.map((d) => d.length),
            legends: sinisterDurationLabelsArray.map((label, i) => ({
                element: squareLabel(colors[i]),
                label: intl.formatMessage(label),
            })),
        };
    }, [sinisterGroup, intl, squareLabel]);

    const stateData = useMemo(() => {
        const colors = [defaultColors[0], defaultColors[4], '#FFD800'];
        const values = Object.values(stateGroup);

        return {
            colors,
            data: calculeDateGroup(values),
            amount: values.map((d) => d.length),
            legends: statusLabelsArray.map((label, i) => ({
                element: squareLabel(colors[i]),
                label: intl.formatMessage(label),
            })),
        };
    }, [stateGroup, defaultColors, intl, squareLabel]);

    const deviceData = useMemo(() => {
        const colors = [defaultColors[0], defaultColors[4]];
        const values = Object.values(deviceGroup);

        return {
            colors,
            data: calculeDateGroup(values),
            amount: values.map((d) => d.length),
            legends: activeLabelsArray.map((label, i) => ({
                id: getDeviceActiveGroupIdByIndex(i),
                element: squareLabel(colors[i]),
                label: intl.formatMessage(label),
            })),
        };
    }, [deviceGroup, defaultColors, intl, squareLabel]);

    const {
        location: {pathname},
    } = history;

    const GoToDetailCommunication = (data) => {
        history.push(
            `${pathname}/chart/communication${buildGroupQuery(
                communicationTime[data] || 'never',
            )}`,
        );
    };

    const GoToDetailSinister = (data) => {
        history.push(
            `${pathname}/chart/sinister${buildGroupQuery(getSinisterGroupIdByIndex(data))}`,
        );
    };

    const GoToDetailState = (data) => {
        history.push(
            `${pathname}/chart/state${buildGroupQuery(getStateGroupIdByIndex(data))}`,
        );
    };

    const GoToDetailDevice = (data) => {
        history.push(
            `${pathname}/chart/device${buildGroupQuery(deviceData.legends[data].id)}`,
        );
    };

    const GoToDetailStatus = ({index, statusIndex}) => {
        const category = typeStatusChartData[index].name || 'default';
        history.push(
            `${pathname}/chart/status${buildGroupQuery(
                `${category}-${getStateGroupIdByIndex(statusIndex)}`,
            )}`,
        );
    };

    return (
        <div className="">
            <Row className="mb-4">
                <Col md="6">
                    <ChartCard
                        header={<span><I18nMessage id="registeredDevices" /></span>}
                        style={{minHeight: 340}}>
                        <HorizontalStackedBar onClick={GoToDetailDevice} {...deviceData} />
                        <LastUpdate/>
                    </ChartCard>
                </Col>

                <Col md="6">
                    <ChartCard
                        header={<span><I18nMessage id="devicesConnectionStatus" /></span>}
                        style={{minHeight: 340}}>
                        <HorizontalStackedBar onClick={GoToDetailState} {...stateData} />
                        <LastUpdate/>
                    </ChartCard>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <ChartCard
                        header={<span><I18nMessage id="lastCommunicationTime" /></span>}
                        style={{minHeight: 340}}>
                        <HorizontalStackedBar
                            onClick={GoToDetailCommunication}
                            {...communicationData}
                        />
                        <LastUpdate/>
                    </ChartCard>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <ChartCard
                        header={<span><I18nMessage id="connectionStatusByCategory" /></span>}
                        style={{height: 340}}>
                        <VerticalBar onClick={GoToDetailStatus} {...statusData} />
                        <LastUpdate/>
                    </ChartCard>
                </Col>
            </Row>

            <Row>
                <Col>
                    <ChartCard
                        header={<span><I18nMessage id="openCasualties" /></span>}
                        style={{minHeight: 340}}>
                        <HorizontalStackedBar
                            onClick={GoToDetailSinister}
                            {...sinisterData}
                        />
                        <LastUpdate/>
                    </ChartCard>
                </Col>
            </Row>
        </div>
    );
};

const LastUpdate = () => {
    const intl = useIntl();

    return (
        <StyledSmall>
            {moment().format(intl.formatMessage({id: 'updatedAtFormat'}))}
        </StyledSmall>
    );
};

export default observer(AdminDashboardContainer);
